import { FC, useState, useMemo, useEffect } from "react";
import { App, Modal, Space, Typography, Table, InputNumber, notification } from "antd";
import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import {
  InventoryLotItem,
  OrderAddV2,
  OrderAddItemV2,
  AddItemLocation,
  InventoryLocation
} from "@/types/inventoryV2";
import { SourceTypeEnum } from "@/types/common";
import { ChangeEnum, OperateEnum } from "@/types/inventory";
import { orderAdd } from "@/apis/inventoryv2";
import { RequirementItem } from "@/types/requirement";
import { AnyObject } from "antd/es/_util/type";
import SText from "@/styles/SText";
import { getLocationCode } from "@/pages/Warehouse/utils";

interface SpecialOutInputProps {
  visible: boolean;
  onClose: () => void;
  selectedRows: InventoryLotItem[];
  showItems?: RequirementItem[];
  reqNumber: string;
}

interface OutRow extends InventoryLotItem {
  outCount: number;
}

const SpecialOutInput: FC<SpecialOutInputProps> = ({
  visible,
  onClose,
  selectedRows,
  showItems,
  reqNumber
}) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const [outLoading, setOutLoading] = useState<boolean>(false);
  const [outRows, setOutRows] = useState<OutRow[]>([]);

  // 根据选中的行初始化 OutRow 数据
  useEffect(() => {
    if (visible && selectedRows.length > 0) {
      const rows: OutRow[] = selectedRows.map(row => ({
        ...row,
        outCount: row.count
      }));
      setOutRows(rows);
    }
  }, [visible, selectedRows]);

  // 更新 outCount
  const handleOutCountChange = (id: string, value: number | null) => {
    console.log('handleOutCountChange:', { id, value });
    setOutRows(prev => prev.map(row =>
      row.id === id ? { ...row, outCount: value ?? 0 } : row
    ));
  };

  const columns: ColumnsType<OutRow> = [
    {
      title: t("purchase.productInfo"),
      children: [
        {
          title: <SText text={t("product.code")} />,
          key: "productCode",
          width: 120,
          render: (_, record) => (
            <SText
              text={record.product?.code || ""}
              style={{ width: "100%" }}
            />
          )
        },
        {
          title: <SText text={t("product.name")} />,
          key: "productName",
          width: 150,
          render: (_, record) => (
            <SText
              text={record.product?.name || ""}
              style={{ width: "100%" }}
            />
          )
        },
        {
          title: <SText text={t("product.brand1")} />,
          key: "productModel",
          width: 120,
          render: (_, record) => (
            <SText
              text={record.product?.model || ""}
              style={{ width: "100%" }}
            />
          )
        },
        {
          title: <SText text={t("product.uom")} />,
          key: "uom",
          width: 80,
          align: "center",
          render: (_, record) => (
            <SText text={record.product?.uom || ""} style={{ width: "100%" }} />
          )
        },
        {
          title: <SText text={t("product.materialProcess")} />,
          key: "materialProcess",
          width: 160,
          render: (_, record) => (
            <SText text={record.product?.process || ""} style={{ width: "100%" }} />
          )
        }
      ]
    },
    {
      title: <SText text={t("warehouse.batchNo")} />,
      key: "lot",
      width: 120,
      render: (_, record) => (
        <SText text={record.lot?.lot || ""} style={{ width: "100%" }} />
      )
    },
    {
      title: <SText text={t("warehouse.thirdPartyBatch")} />,
      key: "thdLot",
      width: 120,
      render: (_, record) => (
        <SText text={record.lot?.thdLot || ""} style={{ width: "100%" }} />
      )
    },
    {
      title: <SText text={t("warehouse.labels")} />,
      dataIndex: "label1",
      key: "label1",
      width: 120,
      align: "center",
      fixed: "right",
      render(_, record) {
        return (
          <SText
            text={(
              record.locations?.map(loc => loc.location?.label1 ?? "") || []
            ).join(", ")}
          />
        );
      }
    },
    {
      title: <SText text={t("warehouse.location")} />,
      key: "locations",
      width: 240,
      align: "center",
      fixed: "right",
      render(_, record) {
        return (
          <SText
            text={(
              record.locations?.map(
                loc => getLocationCode(loc.location) ?? ""
              ) || []
            ).join(", ")}
          />
        );
      }
    },
    {
      title: <SText text={t("order.quantity")} />,
      dataIndex: "count",
      key: "count",
      width: 100,
      align: "center",
      fixed: "right",
      render: text => (
        <SText text={text?.toString() || "0"} style={{ width: "100%" }} />
      )
    },
    {
      title: <SText text={t("warehouse.outNum")} />,
      key: "outCount",
      width: 120,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <InputNumber changeOnWheel
          min={0}
          value={record.outCount}
          onChange={(value) => handleOutCountChange(record.id, value)}
          style={{ width: "100%" }}
          precision={0}
          controls
        />
      )
    },
  ];

  const handleOut = async () => {
    // 处理勾选的数据
    if (outRows.length === 0) {
      notification.warning({
        placement: "top",
        message: t("warehouse.plSelectProduct")
      });
      return;
    }

    // 检查是否有出库数量
    const totalOutCount = outRows.reduce((sum, row) => sum + row.outCount, 0);
    if (totalOutCount === 0) {
      notification.warning({
        placement: "top",
        message: t("warehouse.plEnterOutCount")
      });
      return;
    }

    // 构造 OrderAddV2，使用 outRows 中的 outCount
    const items: OrderAddItemV2[] = outRows
      .filter(row => row.outCount > 0)
      .map(row => {
        // 过滤 count 为 0 的 location 数据
        const locations: AddItemLocation[] = (row.locations || [])
          .filter(loc => loc.count > 0)
          .map(loc => ({
            locationId: loc.location.id,
            count: row.outCount,
            barcodes: loc.barcodes || []
          }));

        return {
          productId: row.productId,
          count: row.outCount,
          lot: row.lot?.lot || "",
          thdlot: row.lot?.thdLot || "",
          desc: "",
          locations,
          sourceId:
            showItems?.find(it => it.productId === row.productId)?.id ?? "",
          sourceType: SourceTypeEnum.REQUIREMENT_ITEM
        };
      });

    const orderData: OrderAddV2 = {
      type: ChangeEnum.PRE_OUT,
      opType: OperateEnum.PICKING,
      desc: "",
      preTime: null,
      arrivalAt: null,
      items
    };

    setOutLoading(true);
    try {
      // 1. 创建出库订单
      const order = await orderAdd(orderData);
      notification.success({
        placement: "top",
        message: t("requirement.createPreOutInventorySuccess"),
        description: order.number
      });
      onClose();
    } catch (addError) {
      notification.error({ placement: "top", message: t("warehouse.outFail") });
    } finally {
      setOutLoading(false);
    }
  };

  return (
    <Modal
      maskClosable={false}
      title={
        <>
          <span>{t("warehouse.specialOutbound")}</span>
          <Typography.Link>{reqNumber}</Typography.Link>
        </>
      }
      open={visible}
      onCancel={onClose}
      onOk={() => {
        modal.confirm({
          title: t("common.tip"),
          content: t("warehouse.confirmPreOut"),
          onOk: () => handleOut()
        });
      }}
      okButtonProps={{ loading: outLoading }}
      okText={t("common.confirm")}
      cancelText={t("common.cancel")}
      width={1200}
    >
      <Table
        columns={columns as ColumnsType<AnyObject>}
        dataSource={outRows}
        rowKey="id"
        pagination={false}
        scroll={{ x: 1300 }}
        size="small"
      />
    </Modal>
  );
};

export default SpecialOutInput;
